/**
 * review.js — Estados de revisión/validación de eventos (Fase 2).
 *
 * Modelo:
 *   Sin revisar (evento cerrado sin reviewStatus)
 *     → "confirmed" (revisor confirma la data tal cual)
 *     → "edited"    (revisor corrigió campos vía el modal Editar evento)
 *       → "validated" (jefe de seguridad valida un caso relevante — evidencia)
 *
 * Roles (por recinto):
 *   - Revisor: supervisor (supervisorUID) o dev interno (devUID).
 *   - Validador: si el recinto tiene chiefUID (jefe de seguridad con cuenta
 *     propia — recintos grandes), SOLO esa cuenta (+dev) valida. Si NO tiene,
 *     el supervisor valida también (recintos donde revisor y jefe son la
 *     misma persona). Cero configuración extra en el caso simple.
 */
import { db } from "../firebase";
import { schema } from "../schema";
import { doc, updateDoc, serverTimestamp, runTransaction } from "firebase/firestore";
import { normalizeResult } from "../components/helpers";

/** Un evento es "relevante" si tuvo hallazgo, patente o sospechoso. */
export function isRelevantEvent(a) {
  if (!a) return false;
  const res = normalizeResult(a.closeResult || a.reasonForResolution);
  return (
    res === "with_findings" ||
    String(a.situation || "") === "6" ||
    !!(a.plate || a.vehicle?.plate) ||
    !!(a.suspect || a.suspectDesc)
  );
}

/** Estado de revisión de un evento cerrado: "pending" | "confirmed" | "edited" | "validated" | null (abierto). */
export function getReviewState(a) {
  if (!a?.resolvedAt) return null;
  return a.reviewStatus || "pending";
}

export const REVIEW_LABELS = {
  pending: "Sin revisar",
  confirmed: "Confirmado",
  edited: "Corregido",
  validated: "Validado",
};

export const REVIEW_COLORS = {
  pending: "#64748b",
  confirmed: "#3b82f6",
  edited: "#f59e0b",
  validated: "#22c55e",
};

/**
 * Roles de revisión del usuario en un recinto.
 * @returns {{ isSup, isDev, isChief, canReview, canValidate }}
 */
export function getReviewRoles(facility, user) {
  const uid = user?.uid;
  const isSup = !!(uid && facility?.supervisorUID && facility.supervisorUID === uid);
  const isDev = !!(uid && facility?.devUID && facility.devUID === uid);
  const isChief = !!(uid && facility?.chiefUID && facility.chiefUID === uid);
  const canReview = isSup || isDev || isChief;
  // Con chiefUID definido, validar es exclusivo del jefe (+dev para pruebas);
  // sin chiefUID, el supervisor cumple ambos roles.
  const canValidate = facility?.chiefUID ? (isChief || isDev) : (isSup || isDev);
  return { isSup, isDev, isChief, canReview, canValidate };
}

const actor = (user) => ({
  name: user?.email || user?.displayName || null,
  uid: user?.uid || null,
});

/** Confirmar: la data del evento está correcta tal cual. */
export async function confirmEvent(a, user) {
  const by = actor(user);
  await updateDoc(doc(db, schema.groupedAlarms, a.docID), {
    reviewStatus: "confirmed",
    reviewedBy: by.name,
    reviewedByUID: by.uid,
    reviewedAt: serverTimestamp(),
    // confirmar de nuevo tras una corrección no borra al validador previo,
    // pero validar es siempre el último paso: si existía, se limpia porque
    // el flujo exige re-validar tras cualquier cambio de estado manual.
    validatedBy: null,
    validatedByUID: null,
    validatedAt: null,
  });
}

/**
 * Reserva el siguiente folio correlativo del recinto: FC-AAAA-NNNN.
 * El contador vive en Facilities/{id}.caseFolio = { year, seq } y se
 * incrementa en transacción (dos validaciones simultáneas no colisionan).
 * Se reinicia cada año.
 */
async function nextCaseFolio(facilityId, year) {
  const facRef = doc(db, `Facilities/${facilityId}`);
  const seq = await runTransaction(db, async (tx) => {
    const snap = await tx.get(facRef);
    const cur = snap.exists() ? snap.data().caseFolio : null;
    const next = cur && cur.year === year ? (cur.seq || 0) + 1 : 1;
    tx.update(facRef, { caseFolio: { year, seq: next } });
    return next;
  });
  return `FC-${year}-${String(seq).padStart(4, "0")}`;
}

/**
 * Validar: evidencia del jefe de seguridad sobre un caso relevante.
 * Implica revisado y asigna folio correlativo (estable en reimpresiones:
 * si el evento ya tenía folio, se conserva).
 *
 * @param {object} a — alarma (debe traer docID)
 * @param {object} user
 * @param {string} [facilityId] — recinto dueño del correlativo; sin él no se asigna folio
 */
export async function validateEvent(a, user, facilityId) {
  const by = actor(user);
  const patch = {
    reviewStatus: "validated",
    validatedBy: by.name,
    validatedByUID: by.uid,
    validatedAt: serverTimestamp(),
  };
  // Si nadie lo había revisado, la validación cuenta también como revisión.
  if (!a.reviewedAt && !a.reviewedBy) {
    patch.reviewedBy = by.name;
    patch.reviewedByUID = by.uid;
    patch.reviewedAt = serverTimestamp();
  }
  // Folio: solo la primera vez (reimprimir no debe renumerar).
  if (!a.caseFolio && facilityId) {
    try {
      patch.caseFolio = await nextCaseFolio(facilityId, new Date().getFullYear());
    } catch (e) {
      console.warn("No se pudo asignar folio correlativo; se valida igualmente.", e);
    }
  }
  await updateDoc(doc(db, schema.groupedAlarms, a.docID), patch);
}
